import { Controller } from "@hotwired/stimulus";
import Cropper from "cropperjs";
import { sanitizeFilename } from "../utils/file_helpers";

export default class extends Controller {
  static targets = [
    "image",
    "form",
    "fileInput",
    "modal",
    "hiddenField",
    "previewImage",
    "spinner",
  ];

  cropper = null;
  cropperInitialized = false;
  originalFilename = null;

  connect() {
    console.log("CropperController connected", {
      hasImageTarget: this.hasImageTarget,
      hasFileInputTarget: this.hasFileInputTarget,
      hasModalTarget: this.hasModalTarget,
    });
    this.setupFileUpload();

    // Add event listener for the uploaded event
    this.element.addEventListener("uploaded", (event) => {
      console.log("Uploaded event received", event);
      this.handleUploaded(event);
    });
  }

  disconnect() {
    console.log("CropperController disconnecting");
    this.destroyCropper();
  }

  setupFileUpload() {
    console.log("Setting up file upload");
    if (this.hasFileInputTarget) {
      console.log("Found file input target");
      this.fileInputTarget.addEventListener("change", (e) =>
        this.handleFileSelect(e),
      );
    } else {
      console.error("File input target not found");
    }
  }

  handleFileSelect(event) {
    console.log("Handling file select", event);
    const file = event.dataTransfer?.files[0] || event.target.files[0];
    if (!file) {
      console.log("No file selected");
      return;
    }

    this.originalFilename = file.name;
    console.log("File selected:", file.name, file.type, file.size);

    // Validate file type and size
    if (!this.isValidFile(file)) return;

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      console.log("File loaded, showing modal");
      this.showModal();

      // Set the image source first
      if (this.hasImageTarget) {
        // Reset any previous image state
        this.resetImageTarget();

        this.imageTarget.src = e.target.result;
        this.imageTarget.onload = () => {
          console.log("Image loaded, initializing cropper");
          // Small delay to ensure modal is fully visible and DOM is ready
          setTimeout(() => {
            this.initializeCropper();
          }, 100);
        };
      } else {
        console.error("Image target not found");
      }
    };
    reader.readAsDataURL(file);
  }

  isValidFile(file) {
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPEG, PNG, GIF, or WEBP)");
      return false;
    }

    if (file.size > maxSize) {
      alert("File size must be less than 10MB");
      return false;
    }

    return true;
  }

  initializeCropper() {
    console.log("Initializing cropper");
    if (!this.hasImageTarget) {
      console.error("Image target not found");
      return;
    }

    // Always destroy any existing cropper first
    this.destroyCropper();

    // Wait a short moment to ensure DOM is ready and previous cropper is fully destroyed
    setTimeout(() => {
      try {
        console.log("Creating new cropper instance");
        console.log("Image target dimensions:", {
          width: this.imageTarget.naturalWidth,
          height: this.imageTarget.naturalHeight,
          visible: this.imageTarget.offsetWidth > 0,
        });

        // Determine if this is a bottle shot cropper based on the modal title or context
        const isBottleShot = this.isBottleShotCropper();

        this.cropper = new Cropper(this.imageTarget, {
          aspectRatio: undefined, // Allow free-form cropping for all images
          viewMode: 0, // Free mode - allow crop box to extend beyond image
          responsive: true,
          autoCropArea: 0.8, // Start with 80% crop area instead of 100%
          dragMode: "move", // Allow moving the image
          cropBoxMovable: true, // Allow moving the crop box
          cropBoxResizable: true, // Allow resizing the crop box
          guides: true, // Show crop guides
          center: true, // Show center indicator
          highlight: false, // Don't highlight crop area
          background: true, // Show background
          zoomable: true, // Allow zooming
          scalable: true, // Allow scaling
          rotatable: true, // Allow rotation
          ready: () => {
            console.log("Cropper ready event fired");
            this.cropperInitialized = true;

            // Set initial crop box to suggested aspect ratio for bottle shots
            if (isBottleShot) {
              this.setSuggestedAspectRatio(1096 / 3709);
            }

            // Add helpful instructions to the modal
            this.addCropperInstructions();
          },
        });
        console.log("Cropper instance created:", this.cropper);
      } catch (error) {
        console.error("Error initializing cropper:", error);
        this.cropperInitialized = false;
      }
    }, 50);
  }

  showModal() {
    console.log("Showing modal");
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden");
      document.body.classList.add("overflow-hidden");
    }
  }

  hideModal() {
    console.log("Hiding modal");
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden");
      document.body.classList.remove("overflow-hidden");
      // Reset modal styles to allow proper reopening
      this.modalTarget.style.removeProperty("display");
      this.modalTarget.style.removeProperty("z-index");
      this.modalTarget.style.removeProperty("pointer-events");
    }
  }

  async submit() {
    console.log("Submitting crop");
    if (
      !this.cropperInitialized ||
      !this.cropper ||
      typeof this.cropper.getCroppedCanvas !== "function"
    ) {
      console.error("Cropper not properly initialized");
      return;
    }

    try {
      // Show spinner
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.remove("hidden");
      }

      console.log("Getting cropped canvas");
      // Get the crop data to calculate output dimensions
      const cropData = this.cropper.getData();
      const isBottleShot = this.isBottleShotCropper();

      let outputWidth, outputHeight;

      if (isBottleShot) {
        // For bottle shots, maintain the specific wine bottle aspect ratio
        const bottleAspectRatio = 1096 / 3709;
        const maxDimension = 3709; // Use the height as max dimension for bottle shots

        outputHeight = maxDimension;
        outputWidth = Math.round(outputHeight * bottleAspectRatio);
      } else {
        // For other images, calculate dimensions while maintaining quality
        const cropAspectRatio = cropData.width / cropData.height;
        const maxDimension = 2000;

        if (cropAspectRatio > 1) {
          // Landscape or square
          outputWidth = Math.min(cropData.width, maxDimension);
          outputHeight = outputWidth / cropAspectRatio;
        } else {
          // Portrait
          outputHeight = Math.min(cropData.height, maxDimension);
          outputWidth = outputHeight * cropAspectRatio;
        }
      }

      const canvas = this.cropper.getCroppedCanvas({
        width: Math.round(outputWidth),
        height: Math.round(outputHeight),
        fillColor: "#fff",
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });

      if (!canvas) {
        console.error("Failed to get cropped canvas");
        return;
      }

      console.log("Converting canvas to blob");
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            console.error("Failed to create blob");
            return;
          }

          try {
            // Get presigned URL
            const presignedResponse = await fetch("/presigned_urls", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector(
                  'meta[name="csrf-token"]',
                )?.content,
              },
              credentials: "same-origin",
            });

            if (!presignedResponse.ok) {
              throw new Error(
                `Failed to get presigned URL: ${presignedResponse.status}`,
              );
            }

            const { url, fields } = await presignedResponse.json();
            console.log("Presigned URL:", url);
            console.log("Presigned fields:", fields);

            // Sanitize filename to remove spaces and special characters for S3 compatibility
            const sanitizedFilename = sanitizeFilename(this.originalFilename);

            // Create FormData with presigned fields
            const formData = new FormData();
            Object.entries(fields).forEach(([key, value]) => {
              formData.append(key, value);
            });

            // Add the file with sanitized filename
            const file = new File([blob], sanitizedFilename, {
              type: blob.type,
              lastModified: new Date().getTime(),
            });
            formData.append("file", file);

            // Upload to S3
            const uploadResponse = await fetch(url, {
              method: "POST",
              body: formData,
            });

            if (!uploadResponse.ok) {
              throw new Error(`Upload failed: ${uploadResponse.status}`);
            }

            // Get the key with the sanitized filename
            const key = fields.key.replace("${filename}", sanitizedFilename);

            // Construct the S3 URL - use the base URL from the presigned response
            const baseUrl = url.split("?")[0]; // Remove query parameters
            const s3Url = `${baseUrl}/${key}`;

            // Update the hidden field and preview image
            this.handleUploaded({ detail: { url: s3Url } });

            console.log("Upload successful:", s3Url);
            this.hideModal();
            this.destroyCropper();
            this.resetFileInput();
            this.resetImageTarget();

            // Debug: Check if form is ready for submission
            this.checkFormReadiness();
          } catch (error) {
            console.error("Upload failed:", error);
            alert(
              error.message ||
                "Failed to upload the cropped image. Please try again.",
            );
          } finally {
            // Hide spinner
            if (this.hasSpinnerTarget) {
              this.spinnerTarget.classList.add("hidden");
            }
          }
        },
        "image/jpeg",
        0.95,
      );
    } catch (error) {
      console.error("Error during crop submission:", error);
      alert("An error occurred while processing the image. Please try again.");
      // Hide spinner on error
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.add("hidden");
      }
    }
  }

  handleUploaded(event) {
    const { url } = event.detail;
    console.log("Uploaded URL:", url);

    // Set the full S3 URL in the hidden field
    const formHiddenField = document.getElementById("vintage_image_url_field");
    if (formHiddenField) {
      formHiddenField.value = url; // Set the full URL, not just the key
      console.log("Set form hidden field value to:", url);
    } else {
      console.error(
        "Form hidden field with id 'vintage_image_url_field' not found!",
      );
    }

    // Update the preview image if it exists (keep full URL for display)
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = url; // Use full URL for display
      this.previewImageTarget.classList.remove("hidden");
      this.previewImageTarget.classList.add("cropper-preview");
      console.log("Updated preview image src to:", url);
    } else {
      console.error("Preview image target not found!");
    }
  }

  cancel() {
    console.log("Canceling crop");
    this.hideModal();
    this.destroyCropper();
    this.resetFileInput();
    this.resetImageTarget();
  }

  destroyCropper() {
    console.log("Destroying cropper");
    console.log("Cropper instance:", this.cropper);
    if (this.cropper && typeof this.cropper.destroy === "function") {
      console.log("Destroying cropper instance");
      try {
        this.cropper.destroy();
      } catch (error) {
        console.error("Error destroying cropper:", error);
      }
      this.cropper = null;
      this.cropperInitialized = false;
    } else {
      console.log("Cropper does not have a function");
    }
  }

  resetFileInput() {
    console.log("Resetting file input");
    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = "";
    }
  }

  resetImageTarget() {
    console.log("Resetting image target");
    if (this.hasImageTarget) {
      this.imageTarget.onload = null;
      this.imageTarget.src = "";
    }
  }

  triggerFileInput(event) {
    console.log("Triggering file input click");
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click();
    } else {
      console.error("File input target not found");
    }
  }

  crop(event) {
    event.preventDefault();
    this.submit();
  }

  checkFormReadiness() {
    console.log("Checking form readiness after crop...");

    // Check if the hidden field has the correct value
    const hiddenField = document.getElementById("vintage_image_url_field");
    if (hiddenField) {
      console.log("Hidden field value:", hiddenField.value);
      console.log("Hidden field name:", hiddenField.name);
    } else {
      console.error("Hidden field not found!");
    }
  }

  isBottleShotCropper() {
    // Check if this is a bottle shot cropper by looking at the modal title
    const modal = this.modalTarget;
    if (!modal) return false;

    const title = modal.querySelector("h3");
    if (title) {
      const titleText = title.textContent.toLowerCase();
      return titleText.includes("bottle") || titleText.includes("bottleshot");
    }

    return false;
  }

  setSuggestedAspectRatio(aspectRatio) {
    // Set initial crop box dimensions based on suggested aspect ratio
    // but still allow free resizing afterwards
    if (!this.cropper) return;

    const containerData = this.cropper.getContainerData();
    const imageData = this.cropper.getImageData();

    // Calculate crop box dimensions that fit the container and match the aspect ratio
    let cropBoxWidth, cropBoxHeight;
    const containerAspect = containerData.width / containerData.height;

    if (aspectRatio > containerAspect) {
      // Aspect ratio is wider than container
      cropBoxWidth = containerData.width * 0.8;
      cropBoxHeight = cropBoxWidth / aspectRatio;
    } else {
      // Aspect ratio is taller than container
      cropBoxHeight = containerData.height * 0.8;
      cropBoxWidth = cropBoxHeight * aspectRatio;
    }

    // Center the crop box
    const left = (containerData.width - cropBoxWidth) / 2;
    const top = (containerData.height - cropBoxHeight) / 2;

    this.cropper.setCropBoxData({
      left: left,
      top: top,
      width: cropBoxWidth,
      height: cropBoxHeight,
    });
  }

  addCropperInstructions() {
    // Find the modal and add instructions above the buttons
    const modal = this.modalTarget;
    if (!modal) return;

    // Look for the button container
    const buttonContainer = modal.querySelector(
      ".flex.justify-end.space-x-3, .mt-4.flex.justify-end.space-x-3",
    );
    if (!buttonContainer) return;

    // Check if instructions already exist
    if (
      buttonContainer.previousElementSibling &&
      buttonContainer.previousElementSibling.classList.contains(
        "cropper-instructions",
      )
    ) {
      return;
    }

    // Create instructions element
    const instructions = document.createElement("div");
    const isBottleShot = this.isBottleShotCropper();

    instructions.className =
      "cropper-instructions mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md";

    if (isBottleShot) {
      instructions.innerHTML = `
        <div class="text-sm text-blue-800">
          <p class="font-medium mb-2">🍷 Bottle Shot Cropping:</p>
          <ul class="list-disc list-inside space-y-1 text-xs">
            <li><strong>Suggested ratio</strong> - Starts with bottle proportions but fully adjustable</li>
            <li><strong>Drag</strong> the image to position the bottle within the crop area</li>
            <li><strong>Resize</strong> the crop box by dragging corners or edges to any size</li>
            <li><strong>Move</strong> the crop box to frame the bottle perfectly</li>
            <li><strong>Zoom</strong> with mouse wheel for precise positioning</li>
          </ul>
          <p class="mt-2 text-xs text-blue-600">💡 Tip: Ensure the entire bottle (including cap and base) fits within the crop area!</p>
        </div>
      `;
    } else {
      instructions.innerHTML = `
         <div class="text-sm text-blue-800">
           <p class="font-medium mb-2">📸 Free Cropping:</p>
          <ul class="list-disc list-inside space-y-1 text-xs">
            <li><strong>Drag</strong> the image to position it within the crop area</li>
            <li><strong>Resize</strong> the crop box by dragging the corners or edges to any size</li>
            <li><strong>Move</strong> the crop box by dragging inside it</li>
            <li><strong>Zoom</strong> with mouse wheel or pinch gestures</li>
            <li><strong>Rotate</strong> the image if needed</li>
          </ul>
          <p class="mt-2 text-xs text-blue-600">💡 Tip: You can crop to any size and shape you need!</p>
        </div>
      `;
    }

    // Insert instructions above the buttons
    buttonContainer.parentNode.insertBefore(instructions, buttonContainer);
  }
}
